import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, query, orderByChild } from "../../Config/matuRtdb";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Crown,
  Calendar,
  BarChart3,
  PieChart,
  Target,
  Award,
  CreditCard,
  Wallet,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Download,
  Filter,
  Sparkles,
  Zap,
  Star
} from "lucide-react";

export default function FinancialReports() {
  const [financialData, setFinancialData] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    weeklyRevenue: 0,
    todayRevenue: 0,
    vipSubscriptions: 0,
    freeUsers: 0,
    conversionRate: 0,
    averageRevenuePerUser: 0
  });
  const [revenueHistory, setRevenueHistory] = useState([]);
  const [userPayments, setUserPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("month");
  const db = getDatabase();

  useEffect(() => {
    loadFinancialData();
  }, [timeRange]);

  const loadFinancialData = () => {
    // Cargar datos de usuarios y pagos
    const usersRef = ref(db, "usuarios");
    const paymentsRef = ref(db, "payments");

    onValue(usersRef, (usersSnapshot) => {
      onValue(paymentsRef, (paymentsSnapshot) => {
        if (usersSnapshot.exists() && paymentsSnapshot.exists()) {
          const users = usersSnapshot.val();
          const payments = paymentsSnapshot.val();

          calculateFinancialMetrics(users, payments);
          generateRevenueHistory(payments);
          generateUserPaymentsList(users, payments);
        }
        setIsLoading(false);
      });
    });
  };

  const calculateFinancialMetrics = (users, payments) => {
    const now = Date.now();
    const today = new Date().setHours(0, 0, 0, 0);
    const weekAgo = today - 7 * 24 * 60 * 60 * 1000;
    const monthAgo = today - 30 * 24 * 60 * 60 * 1000;

    let totalRevenue = 0;
    let monthlyRevenue = 0;
    let weeklyRevenue = 0;
    let todayRevenue = 0;
    let vipUsers = 0;
    let freeUsers = 0;
    let totalUsers = 0;

    // Calcular métricas de usuarios
    Object.values(users || {}).forEach(user => {
      totalUsers++;
      if (user.isVip || user.vip || user.tipoCuenta === "VIP") {
        vipUsers++;
      } else {
        freeUsers++;
      }
    });

    // Calcular métricas de ingresos
    Object.values(payments || {}).forEach(payment => {
      const amount = parseFloat(payment.amount) || 0;
      const timestamp = payment.timestamp || payment.createdAt || 0;

      totalRevenue += amount;

      if (timestamp >= monthAgo) {
        monthlyRevenue += amount;
      }
      if (timestamp >= weekAgo) {
        weeklyRevenue += amount;
      }
      if (timestamp >= today) {
        todayRevenue += amount;
      }
    });

    const conversionRate = totalUsers > 0 ? (vipUsers / totalUsers) * 100 : 0;
    const averageRevenuePerUser = totalUsers > 0 ? totalRevenue / totalUsers : 0;

    setFinancialData({
      totalRevenue,
      monthlyRevenue,
      weeklyRevenue,
      todayRevenue,
      vipSubscriptions: vipUsers,
      freeUsers,
      conversionRate,
      averageRevenuePerUser
    });
  };

  const generateRevenueHistory = (payments) => {
    const history = [];
    const now = Date.now();
    const dayInMs = 24 * 60 * 60 * 1000;
    const daysToShow = timeRange === "week" ? 7 : timeRange === "month" ? 30 : 90;

    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date(now - i * dayInMs);
      const dayStart = date.setHours(0, 0, 0, 0);
      const dayEnd = dayStart + dayInMs;

      let dayRevenue = 0;
      let dayPayments = 0;

      Object.values(payments || {}).forEach(payment => {
        const timestamp = payment.timestamp || payment.createdAt || 0;
        if (timestamp >= dayStart && timestamp < dayEnd) {
          dayRevenue += parseFloat(payment.amount) || 0;
          dayPayments++;
        }
      });

      history.push({
        date: date.toLocaleDateString("es-ES", {
          month: "short",
          day: "numeric"
        }),
        revenue: dayRevenue,
        payments: dayPayments,
        timestamp: dayStart
      });
    }

    setRevenueHistory(history);
  };

  const generateUserPaymentsList = (users, payments) => {
    const userPaymentsList = [];

    Object.entries(payments || {}).forEach(([paymentId, payment]) => {
      const userId = payment.userId || payment.user;
      const user = users[userId];

      if (user) {
        userPaymentsList.push({
          id: paymentId,
          userId,
          userName: user.name || user.email || "Usuario",
          amount: parseFloat(payment.amount) || 0,
          method: payment.method || payment.paymentMethod || "Desconocido",
          status: payment.status || "completed",
          timestamp: payment.timestamp || payment.createdAt || 0,
          userType: user.isVip || user.vip ? "VIP" : "Gratuito"
        });
      }
    });

    userPaymentsList.sort((a, b) => b.timestamp - a.timestamp);
    setUserPayments(userPaymentsList.slice(0, 50)); // Solo los últimos 50
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getGrowthPercentage = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const getGrowthIcon = (percentage) => {
    if (percentage > 0) {
      return <ArrowUpRight className="text-green-400" />;
    } else if (percentage < 0) {
      return <ArrowDownRight className="text-red-400" />;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Reportes Financieros</h2>
          <p className="text-slate-400">Análisis completo de ingresos y métricas financieras</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
          >
            <option value="week">Última Semana</option>
            <option value="month">Último Mes</option>
            <option value="quarter">Último Trimestre</option>
          </select>
          <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 shadow-lg">
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Ingresos Totales</p>
              <p className="text-3xl font-bold text-white">
                {formatCurrency(financialData.totalRevenue)}
              </p>
            </div>
            <div className="p-3 bg-green-900/50 rounded-lg border border-green-700">
              <DollarSign className="h-6 w-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="h-4 w-4 text-green-400 mr-1" />
            <span className="text-green-400">
              +{formatCurrency(financialData.monthlyRevenue)} este mes
            </span>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Ingresos Mensuales</p>
              <p className="text-3xl font-bold text-blue-400">
                {formatCurrency(financialData.monthlyRevenue)}
              </p>
            </div>
            <div className="p-3 bg-blue-900/50 rounded-lg border border-blue-700">
              <Calendar className="h-6 w-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            {getGrowthIcon(getGrowthPercentage(financialData.monthlyRevenue, financialData.weeklyRevenue * 4))}
            <span className="text-slate-300">
              {Math.abs(getGrowthPercentage(financialData.monthlyRevenue, financialData.weeklyRevenue * 4)).toFixed(1)}% vs semana anterior
            </span>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Usuarios VIP</p>
              <p className="text-3xl font-bold text-purple-400">
                {financialData.vipSubscriptions}
              </p>
            </div>
            <div className="p-3 bg-purple-900/50 rounded-lg border border-purple-700">
              <Crown className="h-6 w-6 text-purple-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Target className="h-4 w-4 text-purple-400 mr-1" />
            <span className="text-purple-400">
              {financialData.conversionRate.toFixed(1)}% tasa de conversión
            </span>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">ARPU</p>
              <p className="text-3xl font-bold text-orange-400">
                {formatCurrency(financialData.averageRevenuePerUser)}
              </p>
            </div>
            <div className="p-3 bg-orange-900/50 rounded-lg border border-orange-700">
              <Users className="h-6 w-6 text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Award className="h-4 w-4 text-orange-400 mr-1" />
            <span className="text-orange-400">
              Ingreso promedio por usuario
            </span>
          </div>
        </div>
      </div>

      {/* Gráfico de ingresos */}
      <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Historial de Ingresos</h3>
        <div className="h-64 flex items-end justify-between gap-2">
          {revenueHistory.map((day, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-slate-700 rounded-t-lg relative group">
                <div
                  className="bg-gradient-to-t from-blue-500 to-purple-600 rounded-t-lg transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
                  style={{
                    height: `${Math.max((day.revenue / Math.max(...revenueHistory.map(d => d.revenue))) * 200, 4)}px`
                  }}
                ></div>
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity border border-slate-700">
                  {formatCurrency(day.revenue)}
                </div>
              </div>
              <span className="text-xs text-slate-400 mt-2">{day.date}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Métricas adicionales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Distribución de Usuarios</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium text-slate-300">Usuarios VIP</span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-white">{financialData.vipSubscriptions}</div>
                <div className="text-xs text-slate-400">{financialData.conversionRate.toFixed(1)}% del total</div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-slate-400 rounded-full"></div>
                <span className="text-sm font-medium text-slate-300">Usuarios Gratuitos</span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-white">{financialData.freeUsers}</div>
                <div className="text-xs text-slate-400">{(100 - financialData.conversionRate).toFixed(1)}% del total</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Resumen de Períodos</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">Hoy</span>
              <div className="text-right">
                <div className="text-lg font-bold text-green-400">{formatCurrency(financialData.todayRevenue)}</div>
                <div className="text-xs text-slate-400">Ingresos</div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">Esta Semana</span>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-400">{formatCurrency(financialData.weeklyRevenue)}</div>
                <div className="text-xs text-slate-400">Ingresos</div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">Este Mes</span>
              <div className="text-right">
                <div className="text-lg font-bold text-purple-400">{formatCurrency(financialData.monthlyRevenue)}</div>
                <div className="text-xs text-slate-400">Ingresos</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de pagos recientes */}
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700">
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Pagos Recientes</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Método
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-slate-700">
              {userPayments.slice(0, 10).map((payment) => (
                <tr key={payment.id} className="hover:bg-slate-750 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {payment.userName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">
                          {payment.userName}
                        </div>
                        <div className="text-sm text-slate-400">
                          {payment.userType}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-green-400">
                      {formatCurrency(payment.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-700">
                      {payment.method}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.status === 'completed' ? 'bg-green-900/50 text-green-300 border border-green-700' :
                      payment.status === 'pending' ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700' :
                      'bg-red-900/50 text-red-300 border border-red-700'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    {new Date(payment.timestamp).toLocaleDateString("es-ES")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 